import classnames from 'classnames';
import React from 'react';

import { HorizontalFlagRightSvg } from '../assets/images';
import { Icon } from './Icon';

export interface TagProps {
  children?: React.ReactNode;
  shape?: TagShape;
  icon?: string;
  variant?: TagVariant;
  size?: TagSize;
}

type TagShape =
  | 'vertical-flag'
  | 'horizontal-flag'
  | 'vertical-badge'
  | 'vertical-wider-badge'
  | 'vertical-label';
type TagVariant =
  | 'green-main'
  | 'black-medium'
  | 'yellow-main'
  | 'blue-main'
  | 'pink-main'
  | 'orange-main';
type TagSize = 'md' | 'sm';

export const Tag: React.FC<TagProps> = (props) => {
  switch (props.shape) {
    case 'vertical-label':
      return <VerticalLabelTag {...props} />;
    case 'vertical-badge':
      return <VerticalBadgeTag {...props} />;
    case 'vertical-wider-badge':
      return <VerticalWiderBadgeTag {...props} />;
    case 'horizontal-flag':
      return <HorizontalFlagTag {...props} />;
    case 'vertical-flag':
    default:
      return <VerticalFlagTag {...props} />;
  }
};

const VerticalLabelTag: React.FC<TagProps> = ({ children, icon, variant }) => {
  return (
    <span className={classnames('oe-tag oe-vertical-label', variant && `oe-tag-${variant}`)}>
      <div className="oe-tag-triangle" />
      <div className="oe-tag-content">
        {children}
        <Icon name={icon} />
      </div>
    </span>
  );
};

const VerticalBadgeTag: React.FC<TagProps> = ({ icon, variant, size = 'md' }) => {
  return (
    <span
      className={classnames(
        'oe-tag oe-vertical-badge',
        variant && `oe-tag-${variant}`,
        `oe-tag-${size}`,
      )}
    >
      <div className="oe-tag-triangle" />
      <div className="oe-tag-content">
        <Icon name={icon} />
      </div>
    </span>
  );
};

const VerticalWiderBadgeTag: React.FC<TagProps> = ({ icon, variant, size = 'md' }) => {
  return (
    <span
      className={classnames(
        'oe-tag oe-vertical-wider-badge',
        variant && `oe-tag-${variant}`,
        `oe-tag-${size}`,
      )}
    >
      <div className="oe-tag-triangle" />
      <div className="oe-tag-content">
        <Icon name={icon} />
      </div>
    </span>
  );
};

const VerticalFlagTag: React.FC<TagProps> = ({
  children,
  variant = 'orange-main',
  size = 'md',
}) => {
  return (
    <span className={classnames('oe-tag oe-vertical-flag', variant && `oe-tag-${variant}`)}>
      <div className="oe-tag-triangle" />
      <div className="oe-tag-vertical-content">
        <div className={classnames('oe-tag-content-label', `oe-tag-${size}`)}>{children}</div>
        <div className="oe-tag-content-footer">
          <div className="oe-tag-content-footer-item oe-tag-content-footer-item-left" />
          <div className="oe-tag-content-footer-item oe-tag-content-footer-item-right" />
        </div>
      </div>
    </span>
  );
};

const HorizontalFlagTag: React.FC<TagProps> = ({
  children,
  variant = 'orange-main',
  size = 'md',
}) => {
  return (
    <span className={classnames('oe-tag oe-horizontal-flag', variant && `oe-tag-${variant}`)}>
      <div className="oe-tag-horizontal-content">
        <div className={classnames('oe-tag-content-label', `oe-tag-${size}`)}>{children}</div>
        <div className="oe-tag-content-after">
          <HorizontalFlagRightSvg />
        </div>
      </div>
      <div className="oe-tag-triangle" />
    </span>
  );
};
